import React, { useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { HeartIcon, ShoppingBagIcon, ZapIcon, RulerIcon, StarIcon, TruckIcon, ShieldCheckIcon, RefreshCwIcon, ChevronRightIcon } from 'lucide-react';
import { useStore } from '../context/StoreContext';
import { ImageGallery } from '../components/ImageGallery';
import { SizeChart } from '../components/SizeChart';
import { ReviewSection } from '../components/ReviewSection';
import { ProductCard } from '../components/ProductCard';
import { ShareButton } from '../components/ShareButton';
import { Size } from '../data/products';
const SIZES: Size[] = ['S', 'M', 'L', 'XL', 'XXL'];
export function ProductDetailPage() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const {
    products,
    addToCart,
    toggleWishlist,
    isInWishlist,
    editProduct,
    getApplicablePromo
  } = useStore();
  const product = products.find(p => p.id === id);
  const [selectedSize, setSelectedSize] = useState<Size | null>(null);
  const [sizeError, setSizeError] = useState(false);
  const [showSizeChart, setShowSizeChart] = useState(false);
  const [activeTab, setActiveTab] = useState<'description' | 'reviews' | 'shipping'>('description');
  const [addedToCart, setAddedToCart] = useState(false);
  const relatedProducts = useMemo(() => {
    if (!product) return [];
    return products.filter(p => p.category === product.category && p.id !== product.id).slice(0, 4);
  }, [products, product]);
  if (!product) {
    return <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 px-4" data-id="element-1235">
        <h2 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-1236">
          Product Not Found
        </h2>
        <p className="text-slate-500 mb-6" data-id="element-1237">
          The product you're looking for doesn't exist.
        </p>
        <Link to="/products" className="px-6 py-3 bg-sky-600 text-white font-medium rounded-lg hover:bg-sky-700 transition-colors" data-id="element-1238">
          Browse Products
        </Link>
      </div>;
  }
  const discountPercent = Math.round((product.mrp - product.sellingPrice) / product.mrp * 100);
  const isWishlisted = isInWishlist(product.id);
  const applicablePromo = getApplicablePromo(product.id);
  const handleAddToCart = () => {
    if (!selectedSize) {
      setSizeError(true);
      return;
    }
    addToCart(product, selectedSize);
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2000);
  };
  const handleBuyNow = () => {
    if (!selectedSize) {
      setSizeError(true);
      return;
    }
    addToCart(product, selectedSize);
    navigate('/checkout');
  };
  const handleAddReview = (review: {
    userName: string;
    rating: number;
    comment: string;
  }) => {
    const newReview = {
      id: `r_${Date.now()}`,
      ...review,
      date: new Date().toISOString().split('T')[0]
    };
    editProduct({
      ...product,
      reviews: [newReview, ...product.reviews],
      reviewCount: product.reviewCount + 1,
      ratings: Number(((product.ratings * product.reviewCount + review.rating) / (product.reviewCount + 1)).toFixed(1))
    });
  };
  return <div className="min-h-screen bg-white" data-id="element-1239">
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4" data-id="element-1240">
        <nav className="flex items-center gap-2 text-sm text-slate-500" data-id="element-1241">
          <Link to="/" className="hover:text-sky-600 transition-colors" data-id="element-1242">
            Home
          </Link>
          <ChevronRightIcon className="w-3.5 h-3.5" data-id="element-1243" />
          <Link to="/products" className="hover:text-sky-600 transition-colors" data-id="element-1244">
            Shop
          </Link>
          <ChevronRightIcon className="w-3.5 h-3.5" data-id="element-1245" />
          <Link to={`/products?category=${product.category}`} className="hover:text-sky-600 transition-colors" data-id="element-1246">
            {product.category}
          </Link>
          <ChevronRightIcon className="w-3.5 h-3.5" data-id="element-1247" />
          <span className="text-slate-900 font-medium truncate max-w-[200px]" data-id="element-1248">
            {product.name}
          </span>
        </nav>
      </div>

      {/* Product Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16" data-id="element-1249">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12" data-id="element-1250">
          {/* Image Gallery */}
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.4
        }} data-id="element-1251">
            <ImageGallery images={product.images} alt={product.name} data-id="element-1252" />
          </motion.div>

          {/* Product Info */}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.4,
          delay: 0.1
        }} data-id="element-1253">
            <div className="sticky top-24" data-id="element-1254">
              <span className="inline-block text-xs font-semibold text-sky-600 bg-sky-50 px-3 py-1 rounded-full uppercase tracking-wider mb-3" data-id="element-1255">
                {product.category}
              </span>
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3" data-id="element-1256">
                {product.name}
              </h1>

              {/* Rating */}
              <div className="flex items-center gap-3 mb-5" data-id="element-1257">
                <div className="flex items-center gap-1 bg-green-100 text-green-700 px-2 py-1 rounded text-sm font-medium" data-id="element-1258">
                  <span data-id="element-1259">{product.ratings}</span>
                  <StarIcon className="w-3.5 h-3.5 fill-current" data-id="element-1260" />
                </div>
                <span className="text-sm text-slate-500" data-id="element-1261">
                  {product.reviewCount} Reviews
                </span>
              </div>

              {/* Price */}
              <div className="flex items-baseline gap-3 mb-6 pb-6 border-b border-slate-200" data-id="element-1262">
                <span className="text-3xl font-bold text-slate-900" data-id="element-1263">
                  ₹{product.sellingPrice}
                </span>
                {product.mrp > product.sellingPrice && <>
                    <span className="text-lg text-slate-400 line-through" data-id="element-1264">
                      ₹{product.mrp}
                    </span>
                    <span className="text-sm font-semibold text-green-600 bg-green-50 px-2 py-0.5 rounded" data-id="element-1265">
                      {discountPercent}% OFF
                    </span>
                  </>}
              </div>

              {/* Size Selection */}
              <div className="mb-6" data-id="element-1266">
                <div className="flex items-center justify-between mb-3" data-id="element-1267">
                  <h3 className="font-semibold text-slate-900" data-id="element-1268">
                    Select Size <span className="text-red-500" data-id="element-1269">*</span>
                  </h3>
                  <button onClick={() => setShowSizeChart(true)} className="flex items-center gap-1 text-sm text-sky-600 hover:text-sky-700 font-medium" data-id="element-1270">
                    <RulerIcon className="w-4 h-4" data-id="element-1271" /> Size Chart
                  </button>
                </div>
                <div className="flex flex-wrap gap-3" data-id="element-1272">
                  {SIZES.map(size => {
                  const stock = product.sizes[size];
                  const isOutOfStock = stock === 0;
                  const isSelected = selectedSize === size;
                  return <button key={size} disabled={isOutOfStock} onClick={() => {
                    setSelectedSize(size);
                    setSizeError(false);
                  }} className={`w-14 h-14 rounded-xl font-semibold text-sm border-2 transition-all ${isOutOfStock ? 'bg-slate-50 text-slate-300 border-slate-100 cursor-not-allowed line-through' : isSelected ? 'bg-sky-600 text-white border-sky-600 shadow-md shadow-sky-200' : 'bg-white text-slate-700 border-slate-200 hover:border-sky-400'}`} data-id="element-1273">
                        {size}
                      </button>;
                })}
                </div>
                {sizeError && <p className="text-red-500 text-sm mt-2 flex items-center gap-1" data-id="element-1274">
                    Please select a size before adding to cart
                  </p>}
                {selectedSize && product.sizes[selectedSize] > 0 && <p className="text-sm text-slate-500 mt-2" data-id="element-1275">
                    {product.sizes[selectedSize] <= 5 ? <span className="text-orange-600 font-medium" data-id="element-1276">
                        Only {product.sizes[selectedSize]} left!
                      </span> : <span className="text-green-600" data-id="element-1277">In Stock</span>}
                  </p>}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 mb-6" data-id="element-1278">
                <button onClick={handleAddToCart} className={`flex-1 flex items-center justify-center gap-2 py-3.5 px-6 rounded-xl font-semibold text-sm transition-all ${addedToCart ? 'bg-green-600 text-white' : 'bg-sky-600 text-white hover:bg-sky-700 shadow-md shadow-sky-200 hover:shadow-lg'}`} data-id="element-1279">
                  <ShoppingBagIcon className="w-5 h-5" data-id="element-1280" />
                  {addedToCart ? 'Added to Cart ✓' : 'Add to Cart'}
                </button>
                <button onClick={handleBuyNow} className="flex-1 flex items-center justify-center gap-2 py-3.5 px-6 bg-orange-500 text-white rounded-xl font-semibold text-sm hover:bg-orange-600 transition-colors shadow-md shadow-orange-200 hover:shadow-lg" data-id="element-1281">
                  <ZapIcon className="w-5 h-5" data-id="element-1282" />
                  Buy Now
                </button>
              </div>

              {/* Wishlist & Share */}
              <div className="flex gap-3 mb-8" data-id="element-1283">
                <button onClick={() => toggleWishlist(product.id)} className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border-2 font-medium text-sm transition-all ${isWishlisted ? 'border-red-200 bg-red-50 text-red-600' : 'border-slate-200 text-slate-600 hover:border-red-200 hover:text-red-500'}`} data-id="element-1284">
                  <HeartIcon className={`w-5 h-5 ${isWishlisted ? 'fill-current' : ''}`} data-id="element-1285" />
                  {isWishlisted ? 'Saved to Wishlist' : 'Add to Wishlist'}
                </button>

                <div className="flex-shrink-0" data-id="element-1286">
                  <ShareButton productId={product.id} productName={product.name} productPrice={product.sellingPrice} promoCode={applicablePromo?.code} className="h-full px-4 border-2 border-slate-200 rounded-xl hover:border-sky-200 hover:bg-sky-50" data-id="element-1287" />
                </div>
              </div>

              {/* Features */}
              <div className="grid grid-cols-3 gap-4 p-4 bg-slate-50 rounded-xl" data-id="element-1288">
                <div className="text-center" data-id="element-1289">
                  <TruckIcon className="w-5 h-5 text-sky-600 mx-auto mb-1" data-id="element-1290" />
                  <p className="text-xs text-slate-600 font-medium" data-id="element-1291">
                    Free Delivery
                  </p>
                  <p className="text-[10px] text-slate-400" data-id="element-1292">Above ₹999</p>
                </div>
                <div className="text-center" data-id="element-1293">
                  <RefreshCwIcon className="w-5 h-5 text-sky-600 mx-auto mb-1" data-id="element-1294" />
                  <p className="text-xs text-slate-600 font-medium" data-id="element-1295">
                    Easy Returns
                  </p>
                  <p className="text-[10px] text-slate-400" data-id="element-1296">7 Day Policy</p>
                </div>
                <div className="text-center" data-id="element-1297">
                  <ShieldCheckIcon className="w-5 h-5 text-sky-600 mx-auto mb-1" data-id="element-1298" />
                  <p className="text-xs text-slate-600 font-medium" data-id="element-1299">
                    COD Available
                  </p>
                  <p className="text-[10px] text-slate-400" data-id="element-1300">Pay on Delivery</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Tabs */}
        <div className="mt-12 md:mt-16" data-id="element-1301">
          <div className="border-b border-slate-200" data-id="element-1302">
            <div className="flex gap-8" data-id="element-1303">
              <button onClick={() => setActiveTab('description')} className={`pb-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'description' ? 'border-sky-600 text-sky-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-id="element-1304">
                Description
              </button>
              <button onClick={() => setActiveTab('reviews')} className={`pb-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'reviews' ? 'border-sky-600 text-sky-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-id="element-1305">
                Reviews ({product.reviewCount})
              </button>
              <button onClick={() => setActiveTab('shipping')} className={`pb-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'shipping' ? 'border-sky-600 text-sky-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-id="element-1306">
                Shipping
              </button>
            </div>
          </div>

          <div className="py-8" data-id="element-1307">
            {activeTab === 'description' && <div className="max-w-3xl" data-id="element-1308">
                <p className="text-slate-700 leading-relaxed" data-id="element-1309">
                  {product.description}
                </p>
                <div className="mt-6 space-y-2" data-id="element-1310">
                  <h4 className="font-semibold text-slate-900" data-id="element-1311">
                    Product Details
                  </h4>
                  <ul className="text-sm text-slate-600 space-y-1" data-id="element-1312">
                    <li data-id="element-1313">
                      • Category: {product.category} / {product.subcategory}
                    </li>
                    <li data-id="element-1314">
                      • Available Sizes:{' '}
                      {SIZES.filter(s => product.sizes[s] > 0).join(', ')}
                    </li>
                    <li data-id="element-1315">• Tags: {product.tags.join(', ')}</li>
                  </ul>
                </div>
              </div>}
            {activeTab === 'reviews' && <ReviewSection reviews={product.reviews} ratings={product.ratings} reviewCount={product.reviewCount} onAddReview={handleAddReview} data-id="element-1316" />}
            {activeTab === 'shipping' && <div className="max-w-3xl space-y-4 text-sm text-slate-700" data-id="element-1317">
                <div data-id="element-1318">
                  <h4 className="font-semibold text-slate-900 mb-1" data-id="element-1319">
                    Delivery
                  </h4>
                  <p data-id="element-1320">
                    Standard delivery within 5-7 business days. Free shipping on
                    orders above ₹999.
                  </p>
                </div>
                <div data-id="element-1321">
                  <h4 className="font-semibold text-slate-900 mb-1" data-id="element-1322">Returns</h4>
                  <p data-id="element-1323">
                    Easy returns within 7 days of delivery. Product must be
                    unused with original tags.
                  </p>
                </div>
                <div data-id="element-1324">
                  <h4 className="font-semibold text-slate-900 mb-1" data-id="element-1325">
                    Cash on Delivery
                  </h4>
                  <p data-id="element-1326">
                    COD available on all orders. Pay when your order arrives at
                    your doorstep.
                  </p>
                </div>
              </div>}
          </div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && <div className="mt-8 pt-8 border-t border-slate-200" data-id="element-1327">
            <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-6" data-id="element-1328">
              You May Also Like
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6" data-id="element-1329">
              {relatedProducts.map(p => <ProductCard key={p.id} product={p} data-id="element-1330" />)}
            </div>
          </div>}
      </div>

      {/* Size Chart Modal */}
      <SizeChart isOpen={showSizeChart} onClose={() => setShowSizeChart(false)} data-id="element-1331" />
    </div>;
}